// Shared transform of GET /api/live/:symbol → the metrics object the Analysis surfaces render.
// Extracted from Home so the standalone Detailed Analysis page (/analysis/:asset) builds the SAME object
// from the same response (one SSOT for field mapping).
/* eslint-disable @typescript-eslint/no-explicit-any */
export function buildMetricsFromLive(result: any): any {
  const m = buildMetricsRaw(result);
  // SR-344: an unratified framework (L2 / unclassified) issues no composite and no pillar score — not even as a
  // diagnostic. Every pillar field is nulled here, once, so no surface can render a provisional number.
  if (m.composite_state === 'NOT_COMPUTED_BY_POLICY') {
    for (const k of ['rating_fundamentals', 'rating_onchain', 'rating_market', 'rating_security', 'rating_regulatory', 'rating_esg', 'institutional_rating', 'rating_overall_ex_estimated']) m[k] = null;
    m.institutional_grade = 'NOT_COMPUTED_BY_POLICY';
  }
  return m;
}

function buildMetricsRaw(result: any): any {
  const liveData = result.data;
  const marketCapValue = liveData.fundamentals?.market_cap_usd
  || liveData.fundamentals?.market_cap
  || liveData.market_cap_usd
  || liveData.market_cap;
  const fdvValue = liveData.fundamentals?.fdv_usd
  || liveData.fundamentals?.fdv
  || liveData.fdv_usd
  || liveData.fdv;

  return {
    last_updated: liveData.timestamp || new Date().toISOString(),
    data_sources: 'CoinGecko, SAA Core aggregation',
    // SR-331: only source families that fed this run — CertiK was removed and never appears.
    data_sources_security: [
      liveData.security?.audit_source === 'github_audit_registry' ? 'GitHub audit registry' : (liveData.security?.audit_coverage != null ? 'curated audit estimate' : null),
      liveData.security?.bug_bounty_status ? 'bug-bounty status (curated)' : null,
      'SAA curated incident ledger',
      liveData.security?.contract_scan_source ? 'GoPlus contract scan' : null,
      'SAA Core',
    ].filter(Boolean).join(' · '),
    data_sources_regulatory: 'SAA Legal Core, regulatory authorities',
    // SR-342: only sources actually used — the class mapping, the registry, and the energy estimate if present.
    // OR-SEC-20261004-73: Environmental requires measured energy evidence — the class mapping is no longer a source.
    data_sources_esg: ['SAA AssetRegistry (consensus class, context)', liveData.esg?.energy_source || null].filter(Boolean).join(' · '),
    security_data_tier: liveData.security?.data_tier || 'ESTIMATED',
    regulatory_data_tier: liveData.regulatory?.data_tier || 'DETERMINISTIC',
    esg_data_tier: liveData.esg?.data_tier || 'ESTIMATED',
    esg_e: liveData.esg?.e ?? null,
    esg_s: liveData.esg?.s ?? null,
    esg_g: liveData.esg?.g ?? null,
    esg_consensus: liveData.esg?.consensus ?? null,
    esg_energy_mix: liveData.esg?.energy_mix_pct ?? null,
    esg_energy_twh: liveData.esg?.energy_annualised_twh ?? null,
    esg_energy_source: liveData.esg?.energy_source ?? null,
    esg_energy_asof: liveData.esg?.energy_as_of ?? null,
    esg_carbon_note: liveData.esg?.carbon_note ?? null,
    price_usd: liveData.fundamentals.price_usd || 0,
    asset_name: liveData.profile?.name ?? null,
    asset_class: liveData.profile?.class ?? null,
    asset_class_label: liveData.profile?.class_label ?? null,
    var_backtest_traffic_light: liveData.price_risk?.var_backtest_traffic_light ?? null, // tail-model validation (Kupiec traffic light)
    evidence_quality: liveData.institutional_analysis?.composite?.confidence_effective ?? null, // available-evidence quality (0..1)
    // Canonical 24h volume: prefer the TDI-recorded figure (trusted CEX → CEX → header,
    // the same basis the report and desk quote) over the raw CoinGecko top-line, so the
    // portal doesn't show a 3–4× higher wash-inflated total than the report.
    volume24h_usd: liveData.tdi?.volume_24h_usd ?? liveData.fundamentals.volume24h_usd ?? 0,
    volume_basis: liveData.volume_basis ?? null,
    total_reported_volume_24h_usd: liveData.total_reported_volume_24h_usd ?? null, // context only (unfiltered)
    market_cap: marketCapValue && marketCapValue > 0
    ? marketCapValue / 1e9
    : null,
    fdv: fdvValue && fdvValue > 0
    ? fdvValue / 1e9
    : null,
    price_change_percentage_24h: liveData.fundamentals?.price_change_percentage_24h ?? null, // P0-17: null, never a fabricated 0 (feed down → '—')
    data_quality_tier: result.data_quality_tier || 'ESTIMATED',
    dev_activity: liveData.fundamentals.dev_activity_score,
    governance: liveData.fundamentals.governance_score,
    active_addresses: typeof liveData.onchain?.active_addresses === 'number' && liveData.onchain.active_addresses > 0 ? liveData.onchain.active_addresses : null, // SR-318: absent ≠ 0
    active_addresses_source: liveData.onchain?.active_addresses_source || null,
    active_addresses_as_of: liveData.onchain?.active_addresses_as_of || null,
    network_scope: liveData.onchain?.network_scope || null,
    gas_gwei: liveData.onchain?.gas_gwei ?? null,
    burnt_fees_total_eth: liveData.onchain?.burnt_fees_total_eth ?? null,
    // Do NOT default a missing on-chain transfer volume to 0 — an absent feed must render
    // as N/A (WITHHELD), never as a fabricated "$0.00B" measurement.
    transaction_volume: liveData.onchain.transfer_volume_usd != null ? liveData.onchain.transfer_volume_usd / 1e9 : null,
    tvl: liveData.onchain?.native_chain_defi_tvl_usd ? liveData.onchain.native_chain_defi_tvl_usd / 1e9 : null,
    volatility_30d: liveData.fundamentals.volatility_30d * 100,
    btc_correlation: liveData.market.correlation_btc ?? null,
    btc_dcc_current: liveData.market?.dyn_corr_btc?.current_corr ?? null,
    btc_dcc_method: liveData.market?.dyn_corr_btc?.method ?? null,
    series_snapshot_sha256: liveData.snapshot_sha256 ?? null,
    evt_shape_xi: liveData.price_risk?.evt_tail?.shape_xi ?? null,
    evt_hill_xi: liveData.price_risk?.evt_tail?.hill_xi ?? null,
    evt_hill_tail_index: liveData.price_risk?.evt_tail?.hill_tail_index ?? null,
    chain_activity: liveData.chain_activity ?? null,
    feed_health: [
    ['OHLC price risk', !!liveData.used_ohlc_price_risk],
    ['On-chain', !!liveData.used_real_on_chain],
    ['CoinGecko tickers', !!liveData.used_coingecko_tickers],
    ['Derivatives', !!liveData.used_derivatives_metrics],
    ['Binance spot book', !!liveData.used_binance_spot_book],
    ['DeFiLlama TVL', !!liveData.used_defi_native_chain_tvl],
    ],
    correlation_spx: liveData.market.correlation_spx ?? null,
    correlation_dxy: liveData.market.correlation_dxy ?? null,
    sentiment_index: liveData.market.sentiment_index ?? null,
    audit_coverage: liveData.security?.audit_coverage ?? null,
    bug_bounty_active: liveData.security?.bug_bounty_status != null ? liveData.security.bug_bounty_status.toLowerCase() === 'active' : null,
    // AVAX full report audit 2026-10-08 (F01): the bounty component's evidence (record date; conflict note from V330).
    bug_bounty_evidence: (Array.isArray(liveData.security?.components) ? liveData.security.components : []).find((c: any) => c?.name === 'bug_bounty')?.evidence ?? null,
    days_since_incident: liveData.security?.days_since_incident ?? null,
    security_score: liveData.security?.security_score ?? null,
    incident_date: liveData.security?.incident_date ?? null,
    incident_name: liveData.security?.incident_name ?? null,
    incident_source: liveData.security?.incident_source ?? null,
    incident_verification: liveData.security?.incident_verification ?? null, // SR-279
    incident_evidence: liveData.security?.incident_evidence ?? [],
    candidate_incident_date: liveData.security?.candidate_incident_date ?? null, // display only, never scored
    candidate_incident_name: liveData.security?.candidate_incident_name ?? null,
    candidate_incident_status: liveData.security?.candidate_incident_status ?? null,
    candidate_incident_note: liveData.security?.candidate_incident_note ?? null,
    security_unscored_dependencies: liveData.security?.unscored_dependencies ?? [], // SR-289: disclosed, never scored
    security_components: liveData.security?.components ?? null,
    security_score_coverage: liveData.security?.score_coverage ?? null,
    security_v2: liveData.security?.v2 ?? null, // Security Evidence Contract W2 (shadow)
    security_audit_source: liveData.security?.audit_source ?? null,
    contract_scan_score: liveData.security?.contract_scan_score ?? null,
    contract_scan_flags: liveData.security?.contract_scan_flags ?? null,
    contract_scan_source: liveData.security?.contract_scan_source ?? null,
    audit_source: liveData.security?.audit_source ?? null,
    audit_report_count: liveData.security?.audit_report_count ?? null,
    audit_repo: liveData.security?.audit_repo ?? null,
    audit_url: liveData.security?.audit_url ?? null,
    audit_samples: liveData.security?.audit_samples ?? null,
    esg_composite: liveData.esg?.composite ?? null,
    // Regulatory status: ONLY real values from the core. When the feed omits a
    // jurisdiction we render null (→ 'N/A'), never a best-case default like
    // 'Compliant'/'Low'/'Under Review' — those would fabricate a compliance posture.
    us_status: (liveData.regulatory?.us_status && liveData.regulatory.us_status.trim()) || (() => {
    const r = liveData.regulatory?.regions?.find((x: { region?: string }) => (x?.region || '').toUpperCase() === 'US' || (x?.region || '').includes('United States'));
    return r?.status ?? null;
    })(),
    us_spot_etp: liveData.regulatory?.us_spot_etp || null,
    us_spot_etp_as_of: liveData.regulatory?.us_spot_etp_as_of || null,
    us_spot_etp_source: liveData.regulatory?.us_spot_etp_source || null,
    eu_status: (liveData.regulatory?.eu_status && liveData.regulatory.eu_status.trim()) || (() => {
    const r = liveData.regulatory?.regions?.find((x: { region?: string }) => (x?.region || '').toUpperCase() === 'EU' || (x?.region || '').includes('European'));
    return r?.status ?? null;
    })(),
    sanction_risk: (liveData.regulatory?.sanction_risk && liveData.regulatory.sanction_risk.trim()) || null,
    regulatory_last_verified: liveData.regulatory?.last_verified || null,
    // SR-137/148/145 (V104): scoped US statement, binding-freshness state, class framework,
    // and developer-activity evidence — read straight from the core, never recomputed here.
    us_scope: liveData.regulatory?.us_scope || null,
    regulatory_stale_not_scoreable: Boolean(liveData.regulatory?.stale_not_scoreable),
    regulatory_stale_reason: liveData.regulatory?.stale_reason || null,
    regulatory_ruleset_updated_at: liveData.regulatory?.ruleset_updated_at || null,
    regulatory_withheld: Boolean(liveData.rating?.regulatory_withheld),
    framework_ref: liveData.rating?.framework_ref || null,
    risk_state: liveData.rating?.risk_level || null, // SR-160 canonical risk state
    risk_state_bridge: liveData.rating?.risk_state ?? null, // risk_state_policy_v2 drivers
    dev_activity_evidence: liveData.fundamentals?.dev_activity ?? null,
    esg_score: liveData.esg?.composite ?? null,
    dao_participation: liveData.esg?.dao_participation_pct ?? null,
    transparency: liveData.esg?.transparency_score ?? null,
    // SR-276/277: a WITHHELD composite has no number and no grade; a framework without an ESG axis has no ESG
    // section. Read straight from the core, never recomputed here.
    composite_withheld: Boolean(liveData.rating?.composite_withheld),
    composite_withheld_reason: liveData.rating?.composite_withheld_reason || null,
    composite_withheld_flags: liveData.rating?.composite_withheld_flags || [],
    composite_withheld_code: liveData.rating?.composite_withheld_code || null,
    composite_missing_axes: Array.isArray(result.composite_missing_axes) ? result.composite_missing_axes : [], // OR-UNI-20261004-01
    coverage_bounds: liveData.rating?.coverage_bounds ?? null,
    scoreable_nominal_coverage: liveData.rating?.scoreable_nominal_coverage ?? null,
    scoreable_coverage_floor: liveData.rating?.scoreable_coverage_floor ?? null,
    esg_applicable: liveData.rating?.pillar_tiers?.esg !== 'NOT_APPLICABLE' && liveData.esg?.data_tier !== 'NOT_APPLICABLE',
    institutional_rating: liveData.rating?.composite_withheld ? null : (liveData.rating.overall ?? null), // no fabricated 86.5 — '—' when absent
    institutional_grade: liveData.rating.grade || null,
    rating_fundamentals: liveData.rating.fundamentals || 0,
    rating_onchain: liveData.rating.onchain || 0,
    // SSOT withhold flag from the backend: false ⇒ no live on-chain feed (e.g. ATOM →
    // Cosmos, a non-EVM chain the ~7-major providers and Blockscout don't cover). The
    // on-chain RATING is then N/A, never a measured 0.0/100.
    rating_onchain_measured: liveData.rating.onchain_measured !== false,
    rating_market: liveData.rating.market || 0,
    market_score_breakdown: liveData.rating.market_score_breakdown ?? null,
    market_model: liveData.rating.market_model ?? null, // SR-198
    market_methodology_id: liveData.rating?.market_methodology_id ?? null, // SR-278
    market_v2_applicability: liveData.rating?.market_v2_applicability ?? null,
    methodology_change: liveData.rating.methodology_change ?? null,
    market_quality: liveData.rating.market_quality ?? null,
    rating_security: liveData.rating.security || 0,
    rating_regulatory: liveData.rating.regulatory || 0,
    // OR-SEC-20261004-73 (amended): Environmental is context in the v3 L1 frameworks — no score, never 0.
    rating_esg: ['WITHHELD', 'NOT_APPLICABLE'].includes(liveData.rating?.pillar_tiers?.esg) || liveData.esg?.data_tier === 'CONTEXT' ? null : (liveData.rating.esg || 0),
    esg_context: liveData.esg?.data_tier === 'CONTEXT',
    rating_raw_weighted: liveData.rating.raw_weighted ?? null,
    rating_sharpe_penalty: liveData.rating.sharpe_penalty ?? null,
    rating_overall_ex_estimated: liveData.rating.overall_ex_estimated ?? null,
    rating_ex_estimated_note: liveData.rating.ex_estimated_note ?? null,
    rating_fg_penalty: liveData.rating.fear_greed_penalty ?? null,
    sharpe_12m: liveData.fundamentals?.sharpe_12m ?? null,
    fear_greed_index: liveData.market?.fear_greed_index ?? null,
    ai_summary: result.ai_summary || null,
    institutional_analysis: liveData.institutional_analysis ?? null,
    narrative_readback: result.narrative_readback ?? null,
    narrative_origin: result.narrative_origin ?? null, // SR-147 canonical enum
    evidence_passport: result.evidence_passport ?? null,
    composite_bridge: result.composite_bridge ?? null,
    pillar_cards: Array.isArray(result.pillar_cards) ? result.pillar_cards : null,
    l2_profile: result.l2_profile ?? null,
    framework_status: liveData.rating?.framework_status ?? null,
    composite_state: liveData.rating?.composite_state ?? null,
    typed_metric_registry: result.typed_metric_registry ?? null,
    snapshot_pin: result.snapshot_pin ?? null,
    // audit 2026-10-08: the dated-series evidence (last completed close, calendar integrity) for the freshness rows
    ohlc_series_meta: liveData.ohlc_series_meta ?? null
  };
}
